import { instantAtFacilityTime } from "@alm/domain";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { guardRoute } from "@/lib/policy/route-guard";
import { CalendarPlus, CalendarView } from "@/lib/icons/messaging-icons";
import { orpc } from "@/lib/orpc";
import { cn } from "@/lib/utils";
import { InboxBackButton } from "@/routes/_authenticated/messaging/-components/home/inbox-back-button";
import { InvitationsStrip } from "@/routes/_authenticated/messaging/meetings/-components/attendees/invitations-strip";
import { CalendarFooter } from "@/routes/_authenticated/messaging/meetings/-components/calendar/calendar-footer";
import { clockOf } from "@/routes/_authenticated/messaging/meetings/-components/calendar/grid-time";
import type { MeetingPrefill } from "@/routes/_authenticated/messaging/meetings/-components/meeting/meeting-form-model";
import {
  keepView,
  MEETING_VIEWS,
  storedView,
  type MeetingView,
} from "@/routes/_authenticated/messaging/meetings/-components/meeting/meeting-model";
import {
  dayIn,
  daysToInstants,
  viewerZone,
} from "@/routes/_authenticated/messaging/meetings/-components/calendar/meeting-time";
import { MeetingsList } from "@/routes/_authenticated/messaging/meetings/-components/list/meetings-list";
import { MeetingsMonth } from "@/routes/_authenticated/messaging/meetings/-components/calendar/meetings-month";
import { MeetingsRail } from "@/routes/_authenticated/messaging/meetings/-components/list/meetings-rail";
import {
  gridDays,
  rangeLabel,
  rangeOf,
  stepAnchor,
} from "@/routes/_authenticated/messaging/meetings/-components/calendar/meetings-range";
import { MeetingsToolbar } from "@/routes/_authenticated/messaging/meetings/-components/list/meetings-toolbar";
import { NextUpBar } from "@/routes/_authenticated/messaging/meetings/-components/list/next-up-bar";
import { ScheduleDialog } from "@/routes/_authenticated/messaging/meetings/-components/calendar/schedule-dialog";
import { TimeGrid } from "@/routes/_authenticated/messaging/meetings/-components/calendar/time-grid";
import { useNow } from "@/routes/_authenticated/messaging/meetings/-components/meeting/use-now";
import { useOrganizerName } from "@/routes/_authenticated/messaging/meetings/-components/attendees/use-organizers";
import { usePrefillAttendees } from "@/routes/_authenticated/messaging/meetings/-components/meeting/use-prefill";
import {
  useSlotDrag,
  type SlotDraft,
} from "@/routes/_authenticated/messaging/meetings/-components/calendar/use-slot-drag";

interface Search {
  schedule?: boolean;
  with?: string;
  title?: string;
  conversationId?: string;
  view?: MeetingView;
  day?: string;
}

const text = (value: unknown) =>
  typeof value === "string" && value.trim() !== "" ? value : undefined;

export const Route = createFileRoute("/_authenticated/messaging/_inbox/meetings/")({
  validateSearch: (search: Record<string, unknown>): Search => ({
    schedule:
      search.schedule === true || search.schedule === "1" || search.schedule === "true"
        ? true
        : undefined,
    with: text(search.with),
    title: text(search.title),
    conversationId: text(search.conversationId),
    view: MEETING_VIEWS.some((view) => view === search.view)
      ? (search.view as MeetingView)
      : undefined,
    day:
      typeof search.day === "string" && /^\d{4}-\d{2}-\d{2}$/.test(search.day)
        ? search.day
        : undefined,
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "meetings"),
  component: MeetingsPage,
});

/** The reader's meetings on a day or a week of hours, a month or a list, with what is next beside
 * them, and scheduling a new one from a picked slot or the button. */
function MeetingsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const zone = viewerZone();
  const now = useNow(60_000);
  const today = dayIn(now, zone);
  const phone = useIsMobile();
  const [view, setViewState] = useState<MeetingView>(() => search.view ?? storedView());
  const [anchor, setAnchor] = useState(() => search.day ?? today);
  const [period, setPeriod] = useState<"upcoming" | "past">("upcoming");
  const [creating, setCreating] = useState<MeetingPrefill | null>(null);
  const prefill = usePrefillAttendees({ conversationId: search.conversationId, with: search.with });
  const organizerName = useOrganizerName();
  const shown: MeetingView = phone && view === "week" ? "day" : view;

  const drag = useSlotDrag((draft: SlotDraft) => {
    const startsAt = instantAtFacilityTime(draft.day, clockOf(draft.start), zone);
    if (startsAt.getTime() < Date.now() - 5 * 60_000) {
      toast.info("That time has passed", { description: "Pick a time from now on." });
      return false;
    }
    setCreating({ day: draft.day, start: clockOf(draft.start), end: clockOf(draft.end) });
    return true;
  });
  const setView = (next: MeetingView) => {
    setViewState(next);
    keepView(next);
    if (search.view || search.day) {
      void navigate({
        search: (prev) => ({ ...prev, view: undefined, day: undefined }),
        replace: true,
      });
    }
  };
  const openDay = (day: string) => {
    setAnchor(day);
    setView("day");
  };
  const pickDay = (day: string) => {
    setAnchor(day);
    if (shown === "list") setView("day");
  };
  const days = rangeOf(shown, anchor, today, period);
  const span = daysToInstants(days.from, days.to, zone);
  const listing = useQuery({
    ...orpc.messaging.meetings.list.queryOptions({
      input: { from: span.from, to: span.to, includeCancelled: true },
    }),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
  const meetings = listing.data ?? [];
  const listed = meetings.filter((meeting) => {
    const ended = new Date(meeting.endsAt).getTime() < now;
    return period === "past" ? ended : !ended;
  });
  const scheduling = creating ?? (search.schedule ? {} : null);
  const closeScheduling = () => {
    setCreating(null);
    drag.clear();
    if (search.schedule) {
      void navigate({
        search: (prev) => ({
          ...prev,
          schedule: undefined,
          with: undefined,
          title: undefined,
          conversationId: undefined,
        }),
        replace: true,
      });
    }
  };
  const hint = (what: "day" | "week" | "month") =>
    `${meetings.length === 0 && !listing.isPending ? `Nothing ${what === "day" ? "on this day" : `this ${what}`}. ` : ""}${
      phone ? "Tap" : "Click or drag on"
    } an empty ${what === "month" ? "day" : "time"} to schedule.`;

  const body = listing.isError ? (
    <ReadFailed
      message="Could not load your meetings."
      error={listing.error}
      onRetry={() => void listing.refetch()}
      retrying={listing.isFetching}
    />
  ) : shown === "list" ? (
    <MeetingsList
      meetings={period === "past" ? listed.toReversed() : listed}
      zone={zone}
      pending={listing.isPending}
      organizerName={organizerName}
      emptyTitle={
        period === "upcoming" ? "No meetings coming up" : "No meetings in the last 90 days"
      }
      emptyDescription="Meetings you organize or are invited to show here, with a Join button when it is time."
    />
  ) : shown === "month" ? (
    <MeetingsMonth
      anchor={anchor}
      meetings={meetings}
      zone={zone}
      today={today}
      phone={phone}
      pending={listing.isPending}
      footer={<CalendarFooter hint={hint("month")} />}
      onOpenDay={openDay}
      onCreateDay={(day) => {
        if (day < today) {
          toast.info("That day has passed", { description: "Pick a day from today on." });
          return;
        }
        setCreating({ day });
      }}
    />
  ) : (
    <TimeGrid
      days={gridDays(shown, anchor)}
      meetings={meetings}
      zone={zone}
      now={now}
      pending={listing.isPending}
      drag={drag}
      label={shown === "day" ? rangeLabel("day", anchor) : `Week of ${rangeLabel("week", anchor)}`}
      footer={<CalendarFooter hint={hint(shown)} />}
      onOpenDay={shown === "week" ? openDay : undefined}
    />
  );

  const fills = shown !== "list";
  return (
    <div className="@container flex min-h-0 flex-1 flex-col">
      <header className="flex shrink-0 items-center gap-2 border-b px-3 py-2 md:px-4">
        <InboxBackButton />
        <CalendarView className="text-muted-foreground size-4" />
        <div className="min-w-0 flex-1">
          <h1 className="text-sm font-semibold">Calendar</h1>
          <p className="text-muted-foreground truncate text-xs max-md:hidden">
            Your meetings, on your own clock ({zone}).
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => setCreating(shown !== "list" && anchor > today ? { day: anchor } : {})}
        >
          <CalendarPlus className="size-4" />
          Schedule meeting
        </Button>
      </header>
      <div className="flex min-h-0 flex-1 flex-col gap-3 p-3 max-md:overflow-y-auto md:p-4">
        <MeetingsToolbar
          view={shown}
          onView={setView}
          label={rangeLabel(shown, anchor)}
          today={today}
          anchor={anchor}
          onPick={pickDay}
          onStep={(by) => setAnchor(stepAnchor(shown, anchor, by))}
          onToday={() => setAnchor(today)}
          period={period}
          onPeriod={setPeriod}
        />
        <div className="grid min-h-0 flex-1 grid-rows-[minmax(0,1fr)] gap-4 @5xl:grid-cols-[minmax(0,1fr)_17rem]">
          <div className="flex min-h-0 min-w-0 flex-col gap-3">
            <InvitationsStrip zone={zone} />
            <NextUpBar zone={zone} now={now} />
            <div className={cn("flex min-h-0 flex-1 flex-col", !fills && "overflow-y-auto")}>
              {body}
            </div>
          </div>
          <MeetingsRail
            zone={zone}
            now={now}
            today={today}
            anchor={anchor}
            view={shown}
            onPick={pickDay}
          />
        </div>
      </div>
      {scheduling && prefill.ready ? (
        <ScheduleDialog
          meeting={null}
          prefill={{ ...scheduling, title: search.title, attendees: prefill.attendees }}
          onClose={closeScheduling}
        />
      ) : null}
    </div>
  );
}
